import { useMutation } from '@tanstack/react-query'
import { useContext } from 'react'
import { AxiosContext } from '@/context/Axios'
import { ReactQueryKeys } from '@/types'
import useAppStore from '@/store/useAppStore'
import { ActivityTypeUtil, NotificationItem, SocketEventType } from '@/types/notification'
import { TopicType } from '@/utils/constants'

/** Raw shape of a row from `ajax/users` `eventList` (backed by MySQL
 * `user_events`) — not wire-compatible with the WebSocket `NotificationItem`
 * shape (see useNotificationStorageHook for the socket-side mapping). Only
 * the fields this mapping actually uses are declared. */
interface UserEventRow {
  eventID: string
  eventType: string
  eventDateTime: string
  UserID: string
  UserName: string
  profilePicURL?: string
  profilePicThumbURL?: string
  profilePicFullURL?: string
  objectID?: string
  objName?: string
  secondID?: string
}

const mapEventRow = (row: UserEventRow): NotificationItem => ({
  eventID: row.eventID,
  eventType: row.eventType as SocketEventType,
  eventDateTime: row.eventDateTime,
  userID: '',
  userName: '',
  userFullName: '',
  sourceUserID: row.UserID,
  sourceUserName: row.UserName,
  sourceUserFullName: row.UserName,
  profilePicURL: row.profilePicURL || '',
  profilePicThumbURL: row.profilePicThumbURL || '',
  profilePicFullURL: row.profilePicFullURL || '',
  objectID: row.objectID || '',
  objectName: row.objName || '',
  secondID: row.secondID || '',
  secondName: '',
  thirdID: '',
  thirdName: '',
  variableData: '',
  // `eventList` has no read/unread signal — treat history as already seen so
  // re-fetching on every mount doesn't re-inflate the unread badge with old
  // activity. Only live socket pushes (useNotificationStorageHook) count as
  // genuinely new/unread.
  isRead: true,
  topicApplication: 20,
  // `eventList` doesn't return a topicType at all. `100` isn't a real TopicType
  // (see utils/constants), which broke the click-through link — TopicPreviewBase
  // treats an unrecognized topicType as missing and bounces back to `/`. Albums
  // are the most common target for likes/comments, so default to that instead.
  topicType: TopicType.ALBUM,
  visibility: 1,
  extraData: [],
})

/**
 * REST fallback for notification history. The DynamoDB/WebSocket pipeline
 * (writeNotifyEvents → processNotifySend) is push-on-write only — anything
 * that happened while this browser wasn't connected to the socket was
 * previously just gone. `eventList` reads the same `user_events` table the
 * socket pipeline also logs to, so it's a reasonable (if lower-fidelity —
 * no read/unread, fewer fields) hydration source. Call once per mount of
 * the notification socket (see useNotificationSocket).
 */
export const useGetNotificationHistory = () => {
  const axios = useContext(AxiosContext)
  const { hydrateHistory } = useAppStore()

  return useMutation({
    mutationKey: [ReactQueryKeys.GET_NOTIFICATION_HISTORY],
    mutationFn: async () => {
      const response = await axios.api.post('ajax/users', {
        requestType: 'eventList',
        format: 'json',
        pageNo: 1,
      })
      const rows: UserEventRow[] = response.data?.events || []
      const items = rows
        .map(mapEventRow)
        .filter((item) => ActivityTypeUtil.isSocketEventSupported(item.eventType))
      hydrateHistory(items)
      return items
    },
  })
}

export default useGetNotificationHistory
